/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { getImpersonatableUsers, startImpersonation as apiStartImpersonation, exitImpersonation as apiExitImpersonation } from '@services/adminService';
import { toast } from 'react-toastify';
import type { GodModeContextValue } from '@app-types/context';
import type { User, BaseUser } from '@app-types/models/user';

// Session timeout constants (in milliseconds)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const SESSION_WARNING_MS = 25 * 60 * 1000; // 25 minutes (5 min warning)

const GodModeContext = createContext<GodModeContextValue | null>(null);

const STORAGE_KEYS = {
  ORIGINAL_USER: 'godmode_original_user',
  ORIGINAL_TOKEN: 'godmode_original_token',
  IMPERSONATED_USER: 'impersonated_user',
  IMPERSONATION_TOKEN: 'impersonation_token',
} as const;

export const GodModeProvider = ({ children }: { children: React.ReactNode }) => {
  const queryClient = useQueryClient();
  const [isGodModeAvailable, setIsGodModeAvailable] = useState<boolean>(false);
  const [isImpersonating, setIsImpersonating] = useState<boolean>(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [originalUser, setOriginalUser] = useState<BaseUser | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(true);
  const [sessionTimeRemaining, setSessionTimeRemaining] = useState<number | null>(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState<boolean>(false);

  // Refs for timeout management
  const sessionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionStartTimeRef = useRef<number | null>(null);

  // Clear all session timeouts
  const clearSessionTimeouts = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    sessionStartTimeRef.current = null;
    setSessionTimeRemaining(null);
    setShowTimeoutWarning(false);
  }, []);

  // Start session timeout tracking
  const startSessionTimeout = useCallback((exitCallback: () => Promise<void>) => {
    clearSessionTimeouts();
    sessionStartTimeRef.current = Date.now();

    // Set up warning timeout (25 minutes)
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      toast.warning('Impersonation session will expire in 5 minutes', {
        autoClose: false,
        toastId: 'godmode-timeout-warning'
      });

      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - (sessionStartTimeRef.current ?? 0);
        const remaining = Math.max(0, SESSION_TIMEOUT_MS - elapsed);
        setSessionTimeRemaining(remaining);

        if (remaining <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
          }
        }
      }, 1000);
    }, SESSION_WARNING_MS);

    // Set up auto-exit timeout (30 minutes)
    sessionTimeoutRef.current = setTimeout(() => {
      toast.dismiss('godmode-timeout-warning');
      toast.info('Impersonation session expired');
      exitCallback();
    }, SESSION_TIMEOUT_MS);
  }, [clearSessionTimeouts]);

  // Check if current user has god mode privilege and restore impersonation state on mount
  useEffect(() => {
    // FIRST: Check if we're restoring from an active impersonation session
    // This must be checked before localStorage because during impersonation,
    // localStorage['user'] contains the impersonated user (who may not have hasGodMode)
    const storedImpersonationToken = sessionStorage.getItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
    const storedOriginalUser = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_USER);
    const storedImpersonatedUser = sessionStorage.getItem(STORAGE_KEYS.IMPERSONATED_USER);

    if (storedImpersonationToken && storedOriginalUser && storedImpersonatedUser) {
      // Active impersonation session detected - restore it
      try {
        const origUser = JSON.parse(storedOriginalUser) as BaseUser;
        // Only restore if original user has god mode privilege
        if (origUser.hasGodMode === true) {
          setOriginalUser(origUser);
          setImpersonatedUser(JSON.parse(storedImpersonatedUser) as User);
          setIsImpersonating(true);
          setIsGodModeAvailable(true);
          return; // Don't proceed to normal user check
        }
      } catch (error) {
        console.error('Failed to restore impersonation state:', error);
      }
      // If we get here, clear corrupted/invalid state
      sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATED_USER);
      sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    }

    // Normal flow - check localStorage user for god mode privilege
    const userString = localStorage.getItem('user');

    if (userString) {
      try {
        const user = JSON.parse(userString) as BaseUser;
        const hasGodModePrivilege = user.hasGodMode === true;
        setIsGodModeAvailable(hasGodModePrivilege);

        if (!hasGodModePrivilege) {
          // Clear any stale god mode state if user doesn't have privilege
          sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATED_USER);
          sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
          sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
          sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
        }
      } catch (error) {
        console.error('Failed to parse user data:', error);
      }
    }
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => clearSessionTimeouts();
  }, [clearSessionTimeouts]);

  // Search users for impersonation
  const searchUsers = useCallback(async (query = '', type = '') => {
    if (!isGodModeAvailable) return;

    setIsLoading(true);
    try {
      const response = await getImpersonatableUsers({ search: query, type });
      setAvailableUsers(response.data?.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setAvailableUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [isGodModeAvailable]);

  // Internal exit function (used by timeout and manual exit)
  const performExit = useCallback(async () => {
    setIsLoading(true);
    try {
      // Notify server for audit trail (non-blocking)
      apiExitImpersonation().catch(err => console.warn('Failed to log exit:', err));

      // Restore original user data
      const storedOriginalUser = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_USER);
      const storedOriginalToken = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_TOKEN);

      if (storedOriginalUser && storedOriginalToken) {
        localStorage.setItem('user', storedOriginalUser);
        localStorage.setItem('token', storedOriginalToken);
      }

      // Clear impersonation data
      sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATED_USER);
      sessionStorage.removeItem(STORAGE_KEYS.IMPERSONATION_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);

      // Clear session timeouts
      clearSessionTimeouts();

      setImpersonatedUser(null);
      setOriginalUser(null);
      setIsImpersonating(false);

      toast.success('Exited God Mode');

      // Invalidate all React Query caches to refresh data for the restored user
      await queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to exit God Mode:', error);
      toast.error('Failed to exit God Mode');
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, clearSessionTimeouts]);

  // Start impersonating a user
  const impersonate = useCallback(async (userId: string) => {
    if (!isGodModeAvailable || isImpersonating) return;

    setIsLoading(true);
    try {
      // Save current user and token before impersonation
      const currentUser = localStorage.getItem('user');
      const currentToken = localStorage.getItem('token');

      sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_USER, currentUser ?? '');
      sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_TOKEN, currentToken ?? '');
      setOriginalUser(JSON.parse(currentUser ?? '{}') as BaseUser);

      // Call API to get impersonation token
      const response = await apiStartImpersonation(userId);
      const { token, user } = response.data || response;

      // Store impersonation data in sessionStorage
      sessionStorage.setItem(STORAGE_KEYS.IMPERSONATION_TOKEN, token);
      sessionStorage.setItem(STORAGE_KEYS.IMPERSONATED_USER, JSON.stringify(user));

      // Update localStorage with impersonated user data
      // This ensures components reading from localStorage see the impersonated user
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);

      setImpersonatedUser(user as User);
      setIsImpersonating(true);

      toast.success(`Now impersonating: ${(user as User).displayName || (user as User).email}`);

      // Start session timeout
      startSessionTimeout(performExit);

      // Invalidate all React Query caches to refresh data for the impersonated user
      await queryClient.invalidateQueries();
    } catch (error: unknown) {
      console.error('Failed to start impersonation:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to start impersonation');

      // Clean up on failure
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_USER);
      sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_TOKEN);
    } finally {
      setIsLoading(false);
    }
  }, [isGodModeAvailable, isImpersonating, queryClient, startSessionTimeout, performExit]);

  // Exit impersonation and restore original user
  const exitGodMode = useCallback(async () => {
    if (!isImpersonating) return;
    await performExit();
  }, [isImpersonating, performExit]);

  // Toggle panel visibility
  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => !prev);
  }, []);

  // Dismiss timeout warning
  const dismissTimeoutWarning = useCallback(() => {
    setShowTimeoutWarning(false);
    toast.dismiss('godmode-timeout-warning');
  }, []);

  const value: GodModeContextValue = {
    isGodModeAvailable,
    isImpersonating,
    impersonatedUser,
    originalUser,
    availableUsers,
    isLoading,
    isPanelOpen,
    sessionTimeRemaining,
    showTimeoutWarning,
    searchUsers,
    impersonate,
    exitGodMode,
    togglePanel,
    dismissTimeoutWarning,
  };

  return (
    <GodModeContext.Provider value={value}>
      {children}
    </GodModeContext.Provider>
  );
};

export const useGodMode = (): GodModeContextValue => {
  const context = useContext(GodModeContext);
  if (!context) {
    throw new Error('useGodMode must be used within a GodModeProvider');
  }
  return context;
};

export default GodModeContext;
